import SectionMessage from '@atlaskit/section-message/message';

import SectionMessageAction from '@atlaskit/section-message/message-action';
import { useOriginTracing } from '@post-office/message-maker-browser/use-origin-tracing';

import { useMessageEvent } from '@post-office/message-lifecycle';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import { normalisePaidOfferingName, type PaidOfferingName } from './paid-offerings';

const messages = defineMessages({
	title: {
		id: 'post-office.commerce-subscription-auto-downgrade-ccp.bac-banner.title',
		defaultMessage: 'Your {offeringName} trial has ended',
		description: 'Heading for the BAC banner explaining that a paid product trial has ended.',
	},
	body: {
		id: 'post-office.commerce-subscription-auto-downgrade-ccp.bac-banner.body.with-plan',
		defaultMessage: 'Your {productName} has been downgraded to the {toOfferingName} plan.',
		description:
			'BAC banner text explaining the downgrade. productName is the product and toOfferingName is the destination plan, such as Free or Starter.',
	},
	restorePlan: {
		id: 'post-office.commerce-subscription-auto-downgrade-ccp.bac-banner.restore-plan',
		defaultMessage: 'Restore {offeringName} plan',
		description: 'Link label for restoring the previous paid plan after a trial downgrade.',
	},
});

const getOfferingClickIdentifier = (offeringName: PaidOfferingName) => {
	switch (offeringName) {
		case 'Standard':
			return 'buyStandard';
		case 'Premium':
			return 'buyPremium';
		case 'Business':
			return 'buyBusiness';
		case 'Business + AI':
			return 'buyBusinessAI';
	}
};

export type TrialEndedOfferToBuyBannerContentProps = {
	directBuyUrl: string;
	offeringName: string;
	productName: string;
	toOfferingName?: string;
	onCtaClick: VoidFunction;
	onDismiss: VoidFunction;
};

export const TrialEndedOfferToBuyBannerContent = ({
	directBuyUrl,
	offeringName,
	productName,
	toOfferingName,
	onCtaClick,
	onDismiss,
}: TrialEndedOfferToBuyBannerContentProps): JSX.Element | null => {
	const intl = useIntl();
	const normalisedOfferingName = normalisePaidOfferingName(offeringName, productName);

	if (!productName.trim() || !normalisedOfferingName || !directBuyUrl.trim()) {
		return null;
	}

	return (
		<SectionMessage
			appearance="warning"
			actions={
				<SectionMessageAction href={directBuyUrl} onClick={onCtaClick}>
					<FormattedMessage
						{...messages.restorePlan}
						values={{ offeringName: normalisedOfferingName }}
					/>
				</SectionMessageAction>
			}
			isDismissible
			onDismiss={onDismiss}
			testId="trial-ended-offer-to-buy-banner"
			title={intl.formatMessage(messages.title, { offeringName: normalisedOfferingName })}
		>
			<FormattedMessage
				{...messages.body}
				values={{
					productName: productName.trim(),
					toOfferingName: toOfferingName?.trim() || 'Free',
				}}
			/>
		</SectionMessage>
	);
};

export type TrialEndedOfferToBuyBannerProps = Omit<
	TrialEndedOfferToBuyBannerContentProps,
	'onCtaClick' | 'onDismiss'
> & {
	dismiss: VoidFunction;
};

export const TrialEndedOfferToBuyBanner = ({
	dismiss,
	...contentProps
}: TrialEndedOfferToBuyBannerProps): JSX.Element | null => {
	const { messageDismissed } = useMessageEvent();
	const { onCtaActioned, onClosed } = usePostOfficeUserInteractions();
	const directBuyUrl = useOriginTracing('commerce', contentProps.directBuyUrl.trim());
	const normalisedOfferingName = normalisePaidOfferingName(
		contentProps.offeringName,
		contentProps.productName,
	);

	if (!normalisedOfferingName || !directBuyUrl) {
		return null;
	}

	const handleCtaClick = () => {
		void onCtaActioned({
			interactionIdentifier: getOfferingClickIdentifier(normalisedOfferingName),
		});
	};

	const handleDismiss = () => {
		dismiss();
		void messageDismissed();
		void onClosed({
			interactionIdentifier: 'dismiss',
		});
	};

	return (
		<TrialEndedOfferToBuyBannerContent
			{...contentProps}
			directBuyUrl={directBuyUrl}
			offeringName={normalisedOfferingName}
			onCtaClick={handleCtaClick}
			onDismiss={handleDismiss}
		/>
	);
};
